// T26 — Case Files as a classified-folder swipe deck.
// A Tinder-style stack of manila dossiers: drag left/right to flip through,
// see the folders peeking underneath, tap to open the case and file a report.
// Each case is a question + answer (+ optional image, optional guest subject).
import { el, mount, toast } from "../lib/dom";
import { currentUser } from "../lib/firebase";
import { listCases, progressMap, submitCaseAnswer, type CaseFile } from "../lib/data";
import { nav } from "../components/nav";

export async function renderCases(): Promise<void> {
  const uid = currentUser()!.uid;
  const [cases, prog] = await Promise.all([listCases(), progressMap(uid, "caseProgress")]);
  cases.sort((a, b) => a.title.localeCompare(b.title));
  const solved: Record<string, boolean> = {};
  Object.entries(prog).forEach(([k, v]) => (solved[k] = v.status === "solved"));

  const isMine = (c: CaseFile) => !!c.subjectGuestId && c.subjectGuestId === uid;

  const deck = el("div.deck");
  const modal = el("div.case-modal");
  const modalBody = el("div.case-modal__body");
  modal.append(modalBody);
  modal.addEventListener("click", (e) => { if (e.target === modal) closeModal(); });

  let index = 0;

  // ---- folder card (deck face) ----
  function folderFace(c: CaseFile, depth: number): HTMLElement {
    const face = el("div.folder", {
      style: `transform: translateY(${depth * 14}px) scale(${1 - depth * 0.05}); z-index:${10 - depth}; opacity:${depth > 1 ? 0.7 : 1}`,
    },
      el("div.folder__tab", {}, isMine(c) ? "EYES OFF" : "CLASSIFIED"),
      el("div.folder__no", {}, `CASE ${String(index + 1).padStart(3, "0")} / ${cases.length}`),
      el("div.folder__title", {}, c.title || "Case File"),
    );

    if (solved[c.id]) face.append(el("div.stamp.stamp--green", { style: "top:34px" }, "Cracked"));
    else if (isMine(c)) face.append(el("div.stamp", { style: "top:34px" }, "Sealed"));

    if (c.imageUrl) {
      face.append(el("div.folder__photowrap", {},
        el("div.folder__clip", {}),
        el("img.folder__photo", { src: c.imageUrl, draggable: "false" }),
      ));
    } else {
      face.append(el("div.folder__redact", {},
        el("span", { style: "width:90%" }), el("span", { style: "width:70%" }),
        el("span", { style: "width:80%" }), el("span", { style: "width:55%" })));
    }
    face.append(el("div.folder__hint", {}, isMine(c) ? "Your own dossier" : "Tap to open · swipe to flip"));
    return face;
  }

  function renderDeck(enterAnim = false) {
    if (!cases.length) {
      deck.replaceChildren(el("div.deck__empty.card", {},
        el("span.card__tag", {}, "NO CASES"), el("p.tiny.dim", {}, "No dossiers on file yet, Agent.")));
      return;
    }
    const shown = Math.min(3, cases.length);
    const faces: HTMLElement[] = [];
    // Render back-to-front so the top card is last in the DOM.
    for (let depth = shown - 1; depth >= 0; depth--) {
      const c = cases[(index + depth) % cases.length];
      const face = folderFace(c, depth);
      if (depth === 0) {
        attachGestures(face, c);
        if (enterAnim) face.classList.add("folder--enter");
      }
      faces.push(face);
    }
    deck.replaceChildren(...faces);
  }

  function next() { index = (index + 1) % cases.length; renderDeck(true); }
  function prev() { index = (index - 1 + cases.length) % cases.length; renderDeck(true); }

  // ---- drag / tap gestures on the top folder ----
  function attachGestures(face: HTMLElement, c: CaseFile) {
    let startX = 0, startY = 0, dx = 0, dragging = false, moved = false, t0 = 0;

    face.addEventListener("pointerdown", (e) => {
      // Cancel any enter animation — CSS animations override inline styles in the cascade,
      // so we must remove the class before setting transform via JS.
      face.classList.remove("folder--enter");
      dragging = true; moved = false; startX = e.clientX; startY = e.clientY; t0 = Date.now();
      face.setPointerCapture(e.pointerId);
      face.style.transition = "none";
    });
    face.addEventListener("pointermove", (e) => {
      if (!dragging) return;
      dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) moved = true;
      face.style.transform = `translate(${dx}px, ${dy * 0.15}px) rotate(${dx / 22}deg)`;
    });
    const end = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      const quickTap = !moved && Date.now() - t0 < 350;
      if (quickTap) { face.style.transform = ""; openModal(c); return; }
      if (Math.abs(dx) > 90) {
        // Fling off-screen: left swipe = next (deeper), right swipe = prev (back up).
        const dir = dx > 0 ? 1 : -1;
        const goFn = dx > 0 ? prev : next;
        face.style.transition = "transform 0.38s cubic-bezier(.22,.1,.36,1), opacity 0.28s ease 0.10s";
        face.style.transform = `translate(${dir * 600}px, 50px) rotate(${dir * 24}deg)`;
        face.style.opacity = "0";
        setTimeout(goFn, 340);
      } else {
        face.style.transition = "transform 0.28s ease";
        face.style.transform = "";
      }
      dx = 0;
      void e;
    };
    face.addEventListener("pointerup", end);
    face.addEventListener("pointercancel", end);
  }

  // ---- detail modal ----
  function closeModal() { modal.classList.remove("case-modal--open"); }

  function openModal(c: CaseFile) {
    modalBody.replaceChildren();
    const close = el("button.hud__bell", { style: "float:right;min-width:34px;min-height:34px", onclick: closeModal }, "✕");
    modalBody.append(close);
    modalBody.append(
      el("div.folder__tab", { style: "position:static;display:inline-flex;margin-bottom:8px" },
        solved[c.id] ? "CRACKED" : isMine(c) ? "EYES OFF" : "CLASSIFIED"),
      el("div.folder__title", { style: "margin-top:4px" }, c.title || "Case File"),
    );
    if (c.imageUrl) {
      modalBody.append(el("img", { src: c.imageUrl, style: "width:100%;border-radius:4px;border:3px solid #fff;margin:6px 0;filter:grayscale(0.85) contrast(1.2) sepia(0.2)" }));
    }

    if (isMine(c)) {
      modalBody.append(el("p", {}, "This dossier is about you, Agent — classified from your eyes. Others are trying to crack it."));
      modal.classList.add("case-modal--open");
      return;
    }

    modalBody.append(el("p", {}, c.riddlePrompt));

    if (solved[c.id]) {
      modalBody.append(el("div.stamp.stamp--green", { style: "position:static;display:inline-block;transform:rotate(-4deg);margin-top:8px" }, "Cracked"));
    } else {
      const input = el("input.input", { placeholder: "Your answer…" }) as HTMLInputElement;
      const btn = el("button.btn", { style: "margin-top:10px" }, "Submit report") as HTMLButtonElement;
      async function submit() {
        if (!input.value.trim()) return;
        btn.disabled = true;
        try {
          const res = await submitCaseAnswer({ caseId: c.id, answer: input.value });
          if (res.solved) {
            solved[c.id] = true;
            toast(`Cracked. +${res.intelAwarded ?? 0} intel ◆`, "ok");
            renderDeck();
            openModal(c); // refresh modal to the solved state
          } else { toast("Negative. Not a match.", "err"); btn.disabled = false; }
        } catch (e: unknown) {
          toast((e as { message?: string })?.message ?? "Failed.", "err");
          btn.disabled = false;
        }
      }
      btn.addEventListener("click", submit);
      input.addEventListener("keydown", (e) => (e as KeyboardEvent).key === "Enter" && submit());
      modalBody.append(el("div.field", {}, input), btn);
    }
    modal.classList.add("case-modal--open");
  }

  renderDeck();

  mount(el("div", {},
    el("h2", {}, "// CASE FILES"),
    el("p.tiny.dim", {}, "Swipe through the dossiers. Tap one to open it and file your report for intel."),
    deck,
    modal,
    nav("/cases"),
  ));
}
